"use client";

import PrimaryButtonClicky from "@/app/_components/PrimaryButtonClicky";
import {
  appleProvider,
  auth as firebaseAuth,
  googleProvider,
  microsoftProvider,
} from "@/app/_lib/firebase/client";
import { logger } from "@/app/_lib/logger";
import {
  getCookieValue,
  normalizeReferralCode,
  setCookie,
} from "@/app/_lib/referral-utils";
import {
  applySignInError,
  getAuthErrorMessage,
} from "@/app/_lib/utils/authErrors";
import { getProviderMap } from "@medly/auth-config/auth-providers";
import ProviderButton from "@medly/auth-config/components/ProviderButton";
import {
  getLastLoginMethod,
  setLastLoginMethod,
} from "@medly/auth-config/last-login-method";
import { signInWithPopup } from "firebase/auth";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import Input from "../../_components/Input";
import { storeAcquisitionCookies } from "../_lib/acquisitionCookies";
import {
  isCapacitorNative,
  nativeAppleSignIn,
  nativeGoogleSignIn,
  nativeMicrosoftSignIn,
} from "../_lib/nativeSignIn";
import { sanitizeCallbackUrl } from "../_lib/search-params";
import VerificationCodeStep from "./VerificationCodeStep";

type Step = "form" | "code";

type FieldErrors = {
  email: string | null;
  password: string | null;
  confirmPassword: string | null;
};

type FieldTouched = {
  email: boolean;
  password: boolean;
  confirmPassword: boolean;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REFERRAL_CODE_REGEX = /^MEDLYREP[A-Z]{3}$/;
const REFERRAL_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days
const AUTH_INTENT_COOKIE_NAME = "authIntent";
const AUTH_INTENT_COOKIE_MAX_AGE_SECONDS = 60 * 30; // 30 minutes

const providerMap = getProviderMap("open");

export const Form = ({ formType }: { formType: "signup" | "login" }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [callbackUrl, setCallbackUrl] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState("");
  const [referralError, setReferralError] = useState<string | null>(null);
  const [referralTouched, setReferralTouched] = useState(false);
  const [isReferralOpen, setIsReferralOpen] = useState(false);
  const [isValidatingReferralCode, setIsValidatingReferralCode] =
    useState(false);
  const inviteEmailRef = useRef<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
    email: null,
    password: null,
    confirmPassword: null,
  });
  const [touched, setTouched] = useState<FieldTouched>({
    email: false,
    password: false,
    confirmPassword: false,
  });

  const [step, setStep] = useState<Step>("form");
  const [verificationToken, setVerificationToken] = useState<string | null>(
    null,
  );
  const [verificationCode, setVerificationCode] = useState("");
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [canResendAt, setCanResendAt] = useState<number | null>(null);
  const [resendCooldown, setResendCooldown] = useState<number | null>(null);

  const [lastLogin, setLastLogin] = useState<string | null>(null);
  useEffect(() => {
    if (formType === "login") setLastLogin(getLastLoginMethod());
  }, [formType]);

  const RESEND_COOLDOWN_SECONDS = 120;

  const validateEmail = (value: string): string | null => {
    if (!value) return "Email is required";
    if (!EMAIL_REGEX.test(value)) return "Please enter a valid email address";
    return null;
  };

  const validatePasswordField = (value: string): string | null => {
    if (!value) return "Password is required";
    if (value.length < 8) return "Password must be at least 8 characters";
    return null;
  };

  const validateConfirmPassword = (
    value: string,
    passwordValue: string,
  ): string | null => {
    if (!value) return "Please confirm your password";
    if (value !== passwordValue) return "Passwords do not match";
    return null;
  };

  const handleEmailBlur = async () => {
    setTouched((prev) => ({ ...prev, email: true }));

    const formatError = validateEmail(email);
    if (formatError) {
      setFieldErrors((prev) => ({ ...prev, email: formatError }));
      return;
    }

    if (formType === "signup") {
      try {
        const res = await fetch(
          `/api/auth/check-email?email=${encodeURIComponent(email.trim().toLowerCase())}`,
        );
        const data = await res.json();
        if (data.exists) {
          setFieldErrors((prev) => ({
            ...prev,
            email: "This email is already registered. Please log in instead.",
          }));
          return;
        }
      } catch {
        // If check fails, don't block - let the submit handle it
      }
    }

    setFieldErrors((prev) => ({ ...prev, email: null }));
  };

  const handlePasswordBlur = () => {
    setTouched((prev) => ({ ...prev, password: true }));
    setFieldErrors((prev) => ({
      ...prev,
      password: validatePasswordField(password),
    }));
    if (touched.confirmPassword && confirmPassword) {
      setFieldErrors((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(confirmPassword, password),
      }));
    }
  };

  const handleConfirmPasswordBlur = () => {
    setTouched((prev) => ({ ...prev, confirmPassword: true }));
    if (password && confirmPassword) {
      setFieldErrors((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(confirmPassword, password),
      }));
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email) {
      setFieldErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (touched.password) {
      setFieldErrors((prev) => ({
        ...prev,
        password: validatePasswordField(value),
      }));
    }
    if (touched.confirmPassword && confirmPassword) {
      setFieldErrors((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(confirmPassword, value),
      }));
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (touched.confirmPassword && password) {
      setFieldErrors((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(value, password),
      }));
    }
  };

  const buildPostSigninUrl = (overrideCallbackUrl?: string | null): string => {
    const params = new URLSearchParams();
    params.set("intent", formType === "signup" ? "signup" : "signin");

    const effectiveCallbackUrl =
      overrideCallbackUrl !== undefined ? overrideCallbackUrl : callbackUrl;
    const safeCallbackUrl = sanitizeCallbackUrl(effectiveCallbackUrl);
    if (safeCallbackUrl) {
      params.set("callbackUrl", safeCallbackUrl);
    }

    return `/auth/post-signin?${params.toString()}`;
  };

  const validateReferralCodeValue = async (
    rawCode: string,
  ): Promise<boolean> => {
    const normalized = normalizeReferralCode(rawCode);
    setReferralCode(normalized);

    if (!normalized) {
      setReferralError(null);
      return true;
    }

    if (!REFERRAL_CODE_REGEX.test(normalized)) {
      setReferralError("Referral code must look like MEDLYREPXXX");
      return false;
    }

    setIsValidatingReferralCode(true);
    try {
      const res = await fetch(
        `/api/open/referral/validate?code=${encodeURIComponent(normalized)}`,
      );
      const data = (await res.json()) as { valid?: unknown };
      const isValid = data?.valid === true;

      if (!isValid) {
        setReferralError("That referral code doesn't exist");
        return false;
      }

      setReferralError(null);
      return true;
    } catch {
      setReferralError("Could not validate referral code. Please try again.");
      return false;
    } finally {
      setIsValidatingReferralCode(false);
    }
  };

  const ensureReferralCodeValidIfPresent = async (): Promise<boolean> => {
    const normalized = normalizeReferralCode(referralCode);
    if (!normalized) return true;
    setIsReferralOpen(true);
    setReferralTouched(true);
    const isValid = await validateReferralCodeValue(normalized);
    if (!isValid) return false;

    setCookie("referralCode", normalized, REFERRAL_COOKIE_MAX_AGE_SECONDS);
    setCookie("referrerId", normalized, REFERRAL_COOKIE_MAX_AGE_SECONDS);
    return true;
  };

  useEffect(() => {
    const capture = () => {
      if (typeof window !== "undefined" && window.posthog?.capture) {
        window.posthog.capture("auth_page_viewed", {
          page: formType === "signup" ? "signup" : "login",
        });
        return true;
      }
      return false;
    };

    if (capture()) return;

    // PostHog not ready yet — poll until it loads
    const interval = setInterval(() => {
      if (capture()) clearInterval(interval);
    }, 100);

    return () => clearInterval(interval);
  }, [formType]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const safeCallback = sanitizeCallbackUrl(urlParams.get("callbackUrl"));
    if (safeCallback) setCallbackUrl(safeCallback);
    const ie = urlParams.get("inviteEmail");
    if (ie) {
      inviteEmailRef.current = ie;
      setEmail(ie);
    }
    if (formType !== "signup") return;

    storeAcquisitionCookies(urlParams);

    const ref =
      urlParams.get("ref") ??
      getCookieValue("referralCode") ??
      getCookieValue("referrerId");
    if (!ref) return;
    setReferralCode(normalizeReferralCode(ref));
    setIsReferralOpen(true);
  }, [formType]);

  useEffect(() => {
    if (!expiresAt) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const remaining = Math.max(
        0,
        Math.floor((expiresAt - Date.now()) / 1000),
      );
      setTimeRemaining(remaining);

      if (remaining === 0) {
        setStep("form");
        setVerificationToken(null);
        setVerificationCode("");
        setExpiresAt(null);
        setCanResendAt(null);
        setResendCooldown(null);
        setError("Verification code expired. Please try again.");
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  useEffect(() => {
    if (!canResendAt) {
      setResendCooldown(null);
      return;
    }

    const updateCooldown = () => {
      const remaining = Math.max(
        0,
        Math.floor((canResendAt - Date.now()) / 1000),
      );
      setResendCooldown(remaining);
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);

    return () => clearInterval(interval);
  }, [canResendAt]);

  const validatePassword = () => {
    setTouched({ email: true, password: true, confirmPassword: true });
    const emailError = validateEmail(email);
    const passwordError = validatePasswordField(password);
    const confirmError = validateConfirmPassword(confirmPassword, password);
    setFieldErrors({
      email: emailError,
      password: passwordError,
      confirmPassword: confirmError,
    });
    return !(emailError || passwordError || confirmError);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const maskEmail = (email: string): string => {
    const [local, domain] = email.split("@");
    if (local.length <= 2) {
      return `${local[0]}***@${domain}`;
    }
    return `${local[0]}${local[1]}***@${domain}`;
  };

  const handleStartVerification = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/auth/signup/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      const data = await res.json();

      if (res.status === 409) {
        setError("This email is already registered. Please log in instead.");
        return;
      }

      if (res.status === 400) {
        setError("Please enter a valid email address.");
        return;
      }

      if (!res.ok) {
        setError("Failed to send verification email. Please try again.");
        return;
      }

      const token = data.verification_token;

      if (!data.requires_verification) {
        await handleCredentialsSignIn(token, null);
      } else {
        setVerificationToken(token);
        setExpiresAt(Date.now() + data.expires_in_seconds * 1000);
        setCanResendAt(Date.now() + RESEND_COOLDOWN_SECONDS * 1000);
        setStep("code");
      }
    } catch (err) {
      logger.error("open.auth.error_starting_verification", {
        feature: "auth",
        message: "Error starting verification:",
        error: err,
      });
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setVerificationCode("");
    setError(null);
    await handleStartVerification();
  };

  const handleBackToForm = () => {
    setStep("form");
    setVerificationToken(null);
    setVerificationCode("");
    setExpiresAt(null);
    setCanResendAt(null);
    setResendCooldown(null);
    setError(null);
  };

  const handleSocialSignIn = async (
    providerId: "google" | "apple" | "microsoft",
    /** When true, show a descriptive error instead of rethrowing if
     *  the browser blocks the popup (not a direct user gesture). */
    fallbackOnBlock?: boolean,
    /** Pass explicitly for redirect flows where the closure-captured
     *  referralCode / callbackUrl may be stale. */
    overrideReferralCode?: string | null,
    overrideCallbackUrl?: string | null,
    /** Accumulates all providers tried in this sign-in chain to
     *  prevent A→B→A→B redirect cycles. */
    attemptedProviders?: Set<"google" | "apple" | "microsoft">,
  ) => {
    setIsLoading(true);
    setError(null);
    try {
      if (
        formType === "signup" &&
        !(await ensureReferralCodeValidIfPresent())
      ) {
        return;
      }

      setCookie(
        AUTH_INTENT_COOKIE_NAME,
        formType === "signup" ? "signup" : "signin",
        AUTH_INTENT_COOKIE_MAX_AGE_SECONDS,
      );

      const firebaseProvider =
        providerId === "apple"
          ? appleProvider
          : providerId === "microsoft"
            ? microsoftProvider
            : googleProvider;
      const signInFlag =
        providerId === "apple"
          ? { isAppleSignIn: "true" }
          : providerId === "microsoft"
            ? { isMicrosoftSignIn: "true" }
            : { isGoogleSignIn: "true" };

      const effectiveReferralCode =
        overrideReferralCode !== undefined
          ? overrideReferralCode
          : referralCode;
      const effectiveCallbackUrl =
        overrideCallbackUrl !== undefined ? overrideCallbackUrl : callbackUrl;

      const allAttempted = new Set(attemptedProviders);
      allAttempted.add(providerId);

      // In Capacitor native shell, use native auth SDKs instead of
      // browser popups which lose WebView state.
      const nativeResult =
        isCapacitorNative() && providerId === "apple"
          ? await nativeAppleSignIn()
          : isCapacitorNative() && providerId === "google"
            ? await nativeGoogleSignIn()
            : isCapacitorNative() && providerId === "microsoft"
              ? await nativeMicrosoftSignIn()
              : null;
      if (nativeResult) {
        if (nativeResult.ok) setLastLoginMethod(providerId);
        await processSignInResult(
          nativeResult,
          effectiveReferralCode,
          effectiveCallbackUrl,
          allAttempted,
        );
        return;
      }

      const result = await signInWithPopup(firebaseAuth, firebaseProvider);
      const id_token = await result.user.getIdToken();
      const signInResult = await signIn("credentials", {
        ...signInFlag,
        id_token,
        firebase_refresh_token: result.user.refreshToken,
        redirect: false,
      });
      if (signInResult?.ok) setLastLoginMethod(providerId);
      await processSignInResult(
        signInResult,
        effectiveReferralCode,
        effectiveCallbackUrl,
        allAttempted,
      );
    } catch (err) {
      if (
        fallbackOnBlock &&
        err != null &&
        typeof err === "object" &&
        "code" in err &&
        ((err as { code: string }).code === "auth/popup-blocked" ||
          (err as { code: string }).code === "auth/popup-closed-by-user")
      ) {
        const fallbackCode =
          providerId === "google"
            ? "auth/account-uses-google-signin"
            : providerId === "microsoft"
              ? "auth/account-uses-microsoft-signin"
              : "auth/account-uses-apple-signin";
        setError(getAuthErrorMessage(fallbackCode));
        return;
      }
      applySignInError(err, setError);
    } finally {
      setIsLoading(false);
    }
  };

  const processSignInResult = async (
    result: { error?: string; code?: string; ok?: boolean } | null | undefined,
    overrideReferralCode?: string | null,
    overrideCallbackUrl?: string | null,
    attemptedProviders?: Set<"google" | "apple" | "microsoft">,
  ): Promise<void> => {
    if (result?.error) {
      const errorCode =
        result.code ||
        (typeof result.error === "string"
          ? result.error
          : "auth/unknown-error");

      if (
        errorCode === "auth/account-uses-google-signin" &&
        !attemptedProviders?.has("google")
      ) {
        await handleSocialSignIn(
          "google",
          true,
          overrideReferralCode,
          overrideCallbackUrl,
          attemptedProviders,
        );
        return;
      }
      if (
        errorCode === "auth/account-uses-apple-signin" &&
        !attemptedProviders?.has("apple")
      ) {
        await handleSocialSignIn(
          "apple",
          true,
          overrideReferralCode,
          overrideCallbackUrl,
          attemptedProviders,
        );
        return;
      }
      if (
        errorCode === "auth/account-uses-microsoft-signin" &&
        !attemptedProviders?.has("microsoft")
      ) {
        await handleSocialSignIn(
          "microsoft",
          true,
          overrideReferralCode,
          overrideCallbackUrl,
          attemptedProviders,
        );
        return;
      }

      setError(getAuthErrorMessage(errorCode));
      return;
    }

    if (result?.ok) {
      const effectiveReferralCode =
        overrideReferralCode !== undefined
          ? overrideReferralCode
          : referralCode;
      if (effectiveReferralCode && !referralError) {
        const normalized = normalizeReferralCode(effectiveReferralCode);
        if (normalized) {
          setCookie(
            "referralCode",
            normalized,
            REFERRAL_COOKIE_MAX_AGE_SECONDS,
          );
          setCookie("referrerId", normalized, REFERRAL_COOKIE_MAX_AGE_SECONDS);
        }
      }
      window.location.href = buildPostSigninUrl(overrideCallbackUrl);
    }
  };

  const handleCredentialsSignIn = async (
    token: string | null,
    code: string | null,
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      setCookie(
        AUTH_INTENT_COOKIE_NAME,
        formType === "signup" ? "signup" : "signin",
        AUTH_INTENT_COOKIE_MAX_AGE_SECONDS,
      );

      const result = await signIn("credentials", {
        email: email.trim().toLowerCase(),
        password,
        redirect: false,
        isSignUp: formType === "signup",
        verificationToken: token ?? undefined,
        verificationCode: code ?? undefined,
      });

      if (result?.ok) setLastLoginMethod("credentials");
      await processSignInResult(result);
    } catch (err) {
      applySignInError(err, setError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent, providerId: string) => {
    e.preventDefault();
    if (isLoading) return;

    if (formType === "signup" && providerId === "credentials") {
      if (!validatePassword()) return;

      if (!agreeToTerms) {
        setError("You must agree to the Terms and Conditions");
        return;
      }

      if (!(await ensureReferralCodeValidIfPresent())) {
        return;
      }

      await handleStartVerification();
      return;
    }

    if (providerId === "credentials") {
      await handleCredentialsSignIn(null, null);
      return;
    }

    // Apple, Google, or Microsoft Sign-In via Firebase popup
    if (
      providerId === "apple" ||
      providerId === "google" ||
      providerId === "microsoft"
    ) {
      setFieldErrors({ email: null, password: null, confirmPassword: null });
      setTouched({ email: false, password: false, confirmPassword: false });
      await handleSocialSignIn(providerId);
      return;
    }

    // Other OAuth providers (if any in future)
    setIsLoading(true);
    try {
      setError(null);
      if (
        formType === "signup" &&
        !(await ensureReferralCodeValidIfPresent())
      ) {
        return;
      }

      setCookie(
        AUTH_INTENT_COOKIE_NAME,
        formType === "signup" ? "signup" : "signin",
        AUTH_INTENT_COOKIE_MAX_AGE_SECONDS,
      );

      const callbackUrl = buildPostSigninUrl();
      await signIn(providerId, { callbackUrl });
    } catch (err) {
      applySignInError(err, setError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (verificationCode.length !== 6) {
      setError("Please enter the 6-digit code");
      return;
    }

    if (timeRemaining === 0) {
      setError("Verification code expired. Please request a new code.");
      return;
    }

    await handleCredentialsSignIn(verificationToken, verificationCode);
  };

  // Code verification step UI
  if (step === "code" && formType === "signup") {
    return (
      <VerificationCodeStep
        maskedEmail={maskEmail(email)}
        verificationCode={verificationCode}
        onCodeChange={setVerificationCode}
        onSubmit={handleCodeSubmit}
        onResend={handleResendCode}
        onBack={handleBackToForm}
        isLoading={isLoading}
        error={error}
        timeRemaining={timeRemaining}
        resendCooldown={resendCooldown}
        formatTime={formatTime}
      />
    );
  }

  // Main form UI (email + password)
  return (
    <>
      {providerMap.length > 0 && (
        <>
          <div className="flex flex-col gap-4">
            {providerMap.map((provider) => (
              <ProviderButton
                key={provider.id}
                provider={provider}
                isLoading={isLoading}
                isLastUsed={lastLogin === provider.id}
                onSubmit={(e) => handleSubmit(e, provider.id)}
              />
            ))}
          </div>
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-gray-200"></div>
            <div className="text-center text-sm text-gray-500">or</div>
            <div className="h-px flex-1 bg-gray-200"></div>
          </div>
        </>
      )}
      <form
        ref={formRef}
        method="post"
        className="space-y-4"
        onSubmit={(e) => handleSubmit(e, "credentials")}
      >
        <Input
          id="email"
          name="email"
          type="email"
          label="Email"
          required
          placeholder="Email"
          value={email}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          error={fieldErrors.email}
          touched={touched.email}
          readOnly={!!inviteEmailRef.current}
        />
        <Input
          id="password"
          name="password"
          type="password"
          label="Password"
          required
          placeholder="Password"
          value={password}
          onChange={handlePasswordChange}
          onBlur={handlePasswordBlur}
          error={fieldErrors.password}
          touched={touched.password}
        />

        {formType === "signup" && (
          <>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              label="Confirm Password"
              required
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              onBlur={handleConfirmPasswordBlur}
              error={fieldErrors.confirmPassword}
              touched={touched.confirmPassword}
            />
            <div className="rounded-2xl bg-white">
              <button
                type="button"
                onClick={() => setIsReferralOpen((prev) => !prev)}
                className="cursor-pointer select-none py-3 flex items-center justify-center gap-1 text-sm font-medium w-full"
              >
                <span>Have a referral code?</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  className={`transition-transform duration-200 ${isReferralOpen ? "rotate-180" : ""}`}
                >
                  <path
                    d="M6 8l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div
                className={`grid transition-[grid-template-rows] duration-200 ease-out ${isReferralOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
              >
                <div className="overflow-hidden">
                  <div className="pb-4">
                    <Input
                      id="referralCode"
                      name="referralCode"
                      type="text"
                      label="Referral code"
                      placeholder="MEDLYREPXXX"
                      value={referralCode}
                      onChange={(e) => {
                        setReferralCode(e.target.value);
                        if (referralError) setReferralError(null);
                      }}
                      onBlur={async () => {
                        setReferralTouched(true);
                        await validateReferralCodeValue(referralCode);
                      }}
                      error={referralError}
                      touched={referralTouched}
                    />
                    {isValidatingReferralCode && (
                      <p className="text-xs text-gray-500 mt-2">Validating…</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center items-center gap-2 pb-4">
              <input
                type="checkbox"
                id="terms"
                className="h-3 w-3 rounded border-gray-300 text-primary focus:ring-primary"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                required
              />
              <label htmlFor="terms" className="text-sm">
                I agree to the{" "}
                <a
                  href="https://medlyai.com/uk/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Terms of Use
                </a>{" "}
                and the{" "}
                <a
                  href="https://medlyai.com/uk/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  Privacy Notice
                </a>
              </label>
            </div>
          </>
        )}

        {error && (
          <div className="text-red-500 text-sm text-center">{error}</div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="hidden"
          aria-hidden="true"
        />
        <div
          onClick={() => {
            if (formRef.current && !isLoading) {
              formRef.current.requestSubmit();
            }
          }}
        >
          <PrimaryButtonClicky
            buttonText={formType === "signup" ? "Sign up" : "Log in"}
            disabled={isLoading}
            isLoading={isLoading}
            doesStretch={true}
            showKeyboardShortcut={false}
            buttonState="filled"
          />
        </div>
      </form>
      {formType === "login" && (
        <div className="flex justify-center">
          <Link
            href="/auth/reset-password"
            className="text-sm underline text-center"
          >
            Forgot your password?
          </Link>
        </div>
      )}
    </>
  );
};
